import React from 'react';
import { Box, Grid } from '@material-ui/core';
import { RouteComponentProps } from 'react-router';
import useSystemWideRecentlyOfflineScreens from './useSystemWideRecentlyOfflineScreens';
import useSystemWideRecentlyInactiveGateways from './useSystemWideRecentlyInactiveGateways';
import { Skeleton } from '@material-ui/lab';
import { DefaultTitle } from '@/se/components/collection/CollectionList';
import DashboardCard from './DashboardCard';
import {
  ANALYTICS,
  CHARTING_TABLET,
  EXIT_DISPLAY,
  HOSPITAL_OVERVIEW,
  INTERNAL_MONITOR,
  OPERATION_ROOM_MONITOR,
  PACU_TABLET,
  POST_OP_TABLET,
  PREP_TABLET,
  REVIEW_DISPLAY,
  SCHEDULE_MONITOR,
  STAFF_MONITOR,
  WAITING_ROOM_MONITOR,
  WAITING_ROOM_TABLET,
} from '../entities/screens/enums';

interface SectionProps extends RouteComponentProps {
  systemWideRecentlyOfflineScreens: ReturnType<typeof useSystemWideRecentlyOfflineScreens>;
  systemWideRecentlyInactiveGateways: ReturnType<typeof useSystemWideRecentlyInactiveGateways>;
}

const TABLET_SCREEN_TYPES = new Set<string>([
  PACU_TABLET,
  PREP_TABLET,
  POST_OP_TABLET,
  CHARTING_TABLET,
  WAITING_ROOM_TABLET,
]);

const TV_SCREEN_TYPES = new Set<string>([
  INTERNAL_MONITOR,
  WAITING_ROOM_MONITOR,
  SCHEDULE_MONITOR,
  OPERATION_ROOM_MONITOR,
  REVIEW_DISPLAY,
  EXIT_DISPLAY,
  ANALYTICS,
  HOSPITAL_OVERVIEW,
  STAFF_MONITOR,
]);

const pluralize = (count: number, singular: string, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`;

const getScreenTypeBucket = (screenType: string) => {
  if (TABLET_SCREEN_TYPES.has(screenType)) {
    return 'tablet';
  }

  if (TV_SCREEN_TYPES.has(screenType)) {
    return 'TV screen';
  }

  return 'screen';
};

const getOfflineScreenBreakdown = (
  screens: NonNullable<SectionProps['systemWideRecentlyOfflineScreens']['data']>['systemWideRecentlyOfflineScreens']
) => {
  const counts = screens.reduce<Record<string, number>>((result, screen) => {
    const bucket = getScreenTypeBucket(screen.type.type);
    return {
      ...result,
      [bucket]: (result[bucket] ?? 0) + 1,
    };
  }, {});

  return ['TV screen', 'tablet', 'screen']
    .filter(bucket => counts[bucket] > 0)
    .map(bucket => `${pluralize(counts[bucket], bucket)} offline`)
    .join(', ');
};

const getErrorMessage = (error: unknown) => (error instanceof Error ? error.message : JSON.stringify(error));

const Home = ({
  match,
  history,
  systemWideRecentlyOfflineScreens,
  systemWideRecentlyInactiveGateways,
}: SectionProps) => {
  const offlineScreenItems = systemWideRecentlyOfflineScreens.data?.systemWideRecentlyOfflineScreens ?? [];
  const offlineScreens = offlineScreenItems.length;
  const inactiveGateways = systemWideRecentlyInactiveGateways.data?.systemWideRecentlyInactiveGateways.length ?? 0;
  const offlineScreenBreakdown = getOfflineScreenBreakdown(offlineScreenItems);

  return (
    <>
      <Box mb={2}>
        <DefaultTitle>Dashboard</DefaultTitle>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <DashboardCard
            label="Screen status"
            value={
              systemWideRecentlyOfflineScreens.loading ? (
                <Skeleton variant="text" width="7ch" />
              ) : systemWideRecentlyOfflineScreens.error ? (
                'Unavailable'
              ) : offlineScreens > 0 ? (
                `${offlineScreens} offline`
              ) : (
                'All clear'
              )
            }
            detail={
              systemWideRecentlyOfflineScreens.loading ? (
                <Skeleton variant="text" width="24ch" />
              ) : systemWideRecentlyOfflineScreens.error ? (
                getErrorMessage(systemWideRecentlyOfflineScreens.error)
              ) : offlineScreens > 0 ? (
                offlineScreenBreakdown
              ) : (
                'No offline screens in the last 24 hours'
              )
            }
            success={
              !systemWideRecentlyOfflineScreens.loading &&
              !systemWideRecentlyOfflineScreens.error &&
              offlineScreens === 0
            }
            onClick={() => history.push(match.path + '/screens')}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <DashboardCard
            label="Gateways"
            value={
              systemWideRecentlyInactiveGateways.loading ? (
                <Skeleton variant="text" width="7ch" />
              ) : systemWideRecentlyInactiveGateways.error ? (
                'Unavailable'
              ) : inactiveGateways > 0 ? (
                `${inactiveGateways} inactive`
              ) : (
                'All clear'
              )
            }
            detail={
              systemWideRecentlyInactiveGateways.loading ? (
                <Skeleton variant="text" width="25ch" />
              ) : systemWideRecentlyInactiveGateways.error ? (
                getErrorMessage(systemWideRecentlyInactiveGateways.error)
              ) : inactiveGateways > 0 ? (
                `${pluralize(inactiveGateways, 'inactive gateway')} in the last week`
              ) : (
                'No inactive gateways in the last week'
              )
            }
            success={
              !systemWideRecentlyInactiveGateways.loading &&
              !systemWideRecentlyInactiveGateways.error &&
              inactiveGateways === 0
            }
            onClick={() => history.push(match.path + '/gateways')}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <DashboardCard label="Sensors" value="-" detail="Offline sensors" />
        </Grid>
      </Grid>
    </>
  );
};

export default Home;
